<template>
  <section v-if="state.context.form && state.context.customer" :class="$options.name" class="tw:flex flex-column">
    <div class="tw:p-6">
      <div class="tw:flex tw:flex-wrap tw:sm:flex-nowrap tw:gap-1 tw:w-full">
        <MetricsSmallCard
          :label="trans._inbox.customer.properties.stats.visited"
          :value="state.context.customer.purchasing.visitsCount"
          class="tw:basis-full tw:!shadow-none tw:!rounded-none tw:!border-none"
        />
        <MetricsSmallCard
          :label="trans._inbox.customer.properties.stats.orders"
          :value="state.context.customer.purchasing.ordersCount"
          class="tw:basis-full tw:!shadow-none tw:!rounded-none tw:!border-l-0 tw:!border-t tw:!border-b tw:md:!border-t-0 tw:md:!border-b-0 tw:md:!border-x"
        />
        <MetricsSmallCard
          :label="trans._inbox.customer.properties.stats.aov"
          :value="state.context.customer.purchasing.aov"
          class="tw:basis-full tw:!shadow-none tw:!border-none"
          value-unit="currency"
        />
      </div>
    </div>
    <div class="tw:p-6 tw:flex flex-column tw:gap-4" style="border-top: 1px solid #e5e5e5">
      <h2 class="tw:heading-2">{{ trans._inbox.customer.properties.headers.details }}</h2>
      <EdroneInput
        :label="trans._inbox.customer.properties.email"
        :placeholder="trans._inbox.customer.properties.placeholders.noData"
        :value="state.context.customer.email || ''"
        disabled
        show-copy-button
        unique-id="61f2d7b0-a78d-4742-a8dd-9a6bfbd213c0"
      />
      <div class="tw:flex tw:gap-6">
        <EdroneInput
          :label="trans._inbox.customer.properties.firstName"
          :placeholder="trans._inbox.customer.properties.placeholders.firstName"
          :value="state.context.form.firstName || ''"
          unique-id="fc3d163c-cad7-4f38-b063-83b44421ecfb"
          @input="updateCustomer('firstName', $event)"
        />
        <EdroneInput
          :label="trans._inbox.customer.properties.lastName"
          :placeholder="trans._inbox.customer.properties.placeholders.lastName"
          :value="state.context.form.lastName || ''"
          unique-id="14b56136-766e-48c0-9bf1-e59054fc4543"
          @input="updateCustomer('lastName', $event)"
        />
      </div>
      <EdroneInput
        :label="trans._inbox.customer.properties.phone"
        :placeholder="trans._inbox.customer.properties.placeholders.phone"
        :value="state.context.form.phone || ''"
        show-copy-button
        unique-id="4521e141-8200-4f35-b84c-397e71d38f08"
        @input="updateCustomer('phone', $event)"
      />
      <EdroneInput
        :label="trans._inbox.customer.properties.joined"
        :placeholder="trans._inbox.customer.properties.placeholders.noData"
        :value="getDateFormat(state.context.customer.creationDate)"
        disabled
        unique-id="ca11b3c7-9f63-4885-bb5f-056dde6a56ae"
      >
        <template #tooltip>
          <EdroneTooltip
            :helper="trans._inbox.customer.properties.helpers.joined"
            position="top"
            trigger-event="mouseenter click"
            unique-id="7d332a55-6e25-429e-a61a-384add576ccb"
          />
        </template>
      </EdroneInput>
    </div>

    <div class="tw:p-6 tw:flex flex-column tw:gap-4" style="border-top: 1px solid #e5e5e5">
      <h2 class="tw:heading-2">{{ trans._inbox.customer.properties.headers.subscriptions }}</h2>
      <EdroneSelect
        :key="refreshEmailSelect"
        :allow-empty="false"
        :helper="
          state.context.customer.subscription.emailStatusModificationDate
            ? `${trans._inbox.customer.properties.subscriptions.lastChange} ${getDateFormat(
                state.context.customer.subscription.emailStatusModificationDate
              )}`
            : ''
        "
        :label="trans._inbox.customer.properties.subscriptions.email"
        :options="subscriptionStatusArray"
        :selected-options="subscriptionEmailStatus"
        unique-id="410048be-6a57-4dcb-a1b3-cf90a4685742"
        @select="openDialog('subscriptionEmailStatus', $event.value)"
      >
        <template #tooltip>
          <EdroneTooltip
            position="top"
            trigger-event="mouseenter click"
            type="custom"
            unique-id="ec6ca6b1-3a70-469c-8dc6-76100179fd84"
          >
            <template #custom>
              <a
                class="tw:caption tw:!text-blue-600 tw:underline"
                href="https://help.edrone.me/en/articles/1885291-what-are-transactional-and-marketing-messages#h_d7c9393203"
                target="_blank"
              >
                {{ trans._inbox.customer.properties.subscriptions.link }}
              </a>
            </template>
          </EdroneTooltip>
        </template>
      </EdroneSelect>
      <EdroneSelect
        :key="refreshSMSSelect"
        :allow-empty="false"
        :helper="
          state.context.customer?.subscription.smsStatusModificationDate
            ? `${trans._inbox.customer.properties.subscriptions.lastChange} ${getDateFormat(
                state.context.customer.subscription.smsStatusModificationDate
              )}`
            : ''
        "
        :label="trans._inbox.customer.properties.subscriptions.sms"
        :options="subscriptionStatusArray"
        :selected-options="subscriptionSMSStatus"
        unique-id="f26ad84a-500f-431b-a78f-0ad779067f7d"
        @select="openDialog('subscriptionSMSStatus', $event.value)"
      >
        <template #tooltip>
          <EdroneTooltip
            position="top"
            trigger-event="mouseenter click"
            type="custom"
            unique-id="6aacfc89-f50b-4bf8-ad01-d240a6c9956b"
          >
            <template #custom>
              <a
                class="tw:caption tw:!text-blue-600 tw:underline"
                href="https://help.edrone.me/en/articles/1885291-what-are-transactional-and-marketing-messages#h_d7c9393203"
                target="_blank"
              >
                {{ trans._inbox.customer.properties.subscriptions.link }}
              </a>
            </template>
          </EdroneTooltip>
        </template>
      </EdroneSelect>
      <EdroneSelect
        :key="refreshWhatsappSelect"
        :allow-empty="false"
        :helper="
          state.context.customer?.subscription.whatsappStatusModificationDate
            ? `${trans._inbox.customer.properties.subscriptions.lastChange} ${getDateFormat(
                state.context.customer.subscription.whatsappStatusModificationDate
              )}`
            : ''
        "
        :label="trans._inbox.customer.properties.subscriptions.whatsapp"
        :options="subscriptionStatusArray"
        :selected-options="subscriptionWhatsappStatus"
        unique-id="f26ad84a-500f-431b-a78f-0ad779067f7d"
        @select="openDialog('subscriptionWhatsappStatus', $event.value)"
      >
        <template #tooltip>
          <EdroneTooltip
            position="top"
            trigger-event="mouseenter click"
            type="custom"
            unique-id="6aacfc89-f50b-4bf8-ad01-d240a6c9956b"
          >
            <template #custom>
              <a
                class="tw:caption tw:!text-blue-600 tw:underline"
                href="https://help.edrone.me/en/articles/1885291-what-are-transactional-and-marketing-messages#h_d7c9393203"
                target="_blank"
              >
                {{ trans._inbox.customer.properties.subscriptions.link }}
              </a>
            </template>
          </EdroneTooltip>
        </template>
      </EdroneSelect>
    </div>

    <div class="tw:p-6 tw:flex flex-column tw:gap-4" style="border-top: 1px solid #e5e5e5">
      <h2 class="tw:heading-2">{{ trans._inbox.customer.properties.headers.demographic }}</h2>
      <div class="tw:flex tw:gap-6">
        <EdroneInput
          :label="trans._inbox.customer.properties.country"
          :placeholder="trans._inbox.customer.properties.placeholders.country"
          :value="state.context.form.country || ''"
          unique-id="62c51c23-f87b-474b-b9f0-db5c227bb56b"
          @input="updateCustomer('country', $event)"
        />
        <EdroneInput
          :label="trans._inbox.customer.properties.city"
          :placeholder="trans._inbox.customer.properties.placeholders.city"
          :value="state.context.form.city || ''"
          unique-id="205f4d38-f1b3-4990-b4b3-93c34b518aab"
          @input="updateCustomer('city', $event)"
        />
      </div>
      <EdroneSelect
        :allow-empty="false"
        :label="trans._inbox.customer.properties.language"
        :options="languagesArray"
        :placeholder="trans._inbox.customer.properties.placeholders.language"
        :selected-options="language"
        searchable
        unique-id="c995df94-8bfd-4556-93c7-6e74b8fbdbd2"
        @select="updateCustomer('language', $event.value)"
      />
      <div class="tw:flex tw:gap-6">
        <EdroneDatepicker
          :disabled-date="disabledAfterToday"
          :label="trans._inbox.customer.properties.birthday"
          :placeholder="trans._inbox.customer.properties.placeholders.birthday"
          :value="state.context.form.birthday"
          ignore-timezone
          style="flex: 100%"
          unique-id="70224cf9-987c-44b6-b24f-896e8dfba690"
          @input="updateCustomer('birthday', $event)"
        />
        <EdroneSelect
          :allow-empty="false"
          :label="trans._inbox.customer.properties.gender"
          :options="genderTypeArray"
          :placeholder="trans._inbox.customer.properties.placeholders.gender"
          :selected-options="genderType"
          style="flex: 100%"
          unique-id="331b03cf-11b8-4af6-9408-c4e6a2acc0b8"
          @select="updateCustomer('genderType', $event.value)"
        />
      </div>
      <div class="tw:flex tw:gap-6">
        <EdroneInput
          :label="trans._inbox.customer.properties.userId"
          :placeholder="trans._inbox.customer.properties.placeholders.noData"
          :value="state.context.customer.userId || ''"
          disabled
          unique-id="1f78e296-8de3-4909-a3ad-2372feed991f"
        >
          <template #tooltip>
            <EdroneTooltip
              :helper="trans._inbox.customer.properties.userIdHelper"
              position="top"
              trigger-event="mouseenter click"
              unique-id="ebf55756-1d34-48d4-a680-964d0c1b6301"
            />
          </template>
        </EdroneInput>
        <EdroneInput
          :label="trans._inbox.customer.properties.customerId"
          :placeholder="trans._inbox.customer.properties.placeholders.noData"
          :value="state.context.customer.customerId || ''"
          disabled
          show-copy-button
          unique-id="b6b685aa-5749-419d-8a33-17ac710e15e6"
        >
          <template #tooltip>
            <EdroneTooltip
              :helper="trans._inbox.customer.properties.customerIdHelper"
              position="top"
              trigger-event="mouseenter click"
              unique-id="e2af6241-abc8-45a8-a34f-3e559f37f703"
            />
          </template>
        </EdroneInput>
      </div>
    </div>

    <div class="tw:p-6 tw:flex flex-column tw:gap-4" style="border-top: 1px solid #e5e5e5">
      <h2 class="tw:heading-2">{{ trans._inbox.customer.properties.headers.purchasing }}</h2>
      <div :class="$options.name" style="display: grid; grid-template-columns: 1fr 1fr; align-items: end; gap: 16px">
        <EdroneInput
          :label="trans._inbox.customer.properties.visits"
          :placeholder="trans._inbox.customer.properties.placeholders.noData"
          :value="getInputValue('visitsCount')"
          disabled
          unique-id="e5f536f1-556d-4aaa-92d5-8b83782dd6fc"
        >
          <template #tooltip>
            <EdroneTooltip
              :helper="trans._inbox.customer.properties.helpers.visits"
              position="top"
              trigger-event="mouseenter click"
              unique-id="357a4054-597d-4ae2-9155-16389d011406"
            />
          </template>
        </EdroneInput>
        <EdroneInput
          :label="trans._inbox.customer.properties.lastVisit"
          :placeholder="trans._inbox.customer.properties.placeholders.noData"
          :value="getLastVisitDateFormatted()"
          disabled
          unique-id="1d7d4a82-281c-4b54-8fc6-6030803ef2d0"
        />
        <EdroneInput
          :label="trans._inbox.customer.properties.orders"
          :placeholder="trans._inbox.customer.properties.placeholders.noData"
          :value="getInputValue('ordersCount')"
          disabled
          unique-id="fc087507-5e25-42a6-b13a-d595525438a2"
        >
          <template #tooltip>
            <EdroneTooltip
              :helper="trans._inbox.customer.properties.helpers.orders"
              position="top"
              trigger-event="mouseenter click"
              unique-id="b166ee4b-c36f-4eeb-ab81-4d56f89e49d1"
            />
          </template>
        </EdroneInput>
        <EdroneInput
          :currency="getCurrency()"
          :label="trans._inbox.customer.properties.totalOrdersValue"
          :placeholder="trans._inbox.customer.properties.placeholders.noData"
          :value="getInputValue('totalOrdersValue')"
          disabled
          type="currency"
          unique-id="1efcc083-960c-4525-9942-80b4a381e408"
        />
        <EdroneInput
          :currency="getCurrency()"
          :label="trans._inbox.customer.properties.aov"
          :placeholder="trans._inbox.customer.properties.placeholders.noData"
          :value="getInputValue('aov')"
          disabled
          type="currency"
          unique-id="39847009-29ae-4482-b19e-137c014f248b"
        >
          <template #tooltip>
            <EdroneTooltip
              :helper="trans._inbox.customer.properties.helpers.aov"
              position="top"
              trigger-event="mouseenter click"
              unique-id="57cdbec9-5eb0-4662-80b8-a23e8e8d34ed"
            />
          </template>
        </EdroneInput>
        <EdroneInput
          :currency="getCurrency()"
          :label="trans._inbox.customer.properties.lastOrder"
          :placeholder="trans._inbox.customer.properties.placeholders.noData"
          :value="getInputValue('lastOrderValue')"
          disabled
          type="currency"
          unique-id="883b818f-14e2-43db-b0a5-623529e702bf"
        />
        <EdroneInput
          :label="trans._inbox.customer.properties.purchase"
          :placeholder="trans._inbox.customer.properties.placeholders.noData"
          :value="getInputValue('purchaseValueSegment')"
          disabled
          unique-id="560ce58a-19e7-4ae1-a0da-4eb337be6b50"
        >
          <template #tooltip>
            <EdroneTooltip
              position="top"
              trigger-event="mouseenter click"
              type="custom"
              unique-id="c221f80b-0f5c-4995-abd6-01e169fd4889"
            >
              <template #custom>
                <p class="tw:caption">
                  {{ trans._inbox.customer.properties.helpers.purchase }}
                  <EdroneButtonIcon
                    size="small"
                    type="link"
                    unique-id="3151ba17-db4d-4f35-8cde-0fb7724b3aec"
                    @click="goToDoc"
                  >
                    <font-awesome-icon icon="fa-light fa-link" />
                  </EdroneButtonIcon>
                </p>
              </template>
            </EdroneTooltip>
          </template>
        </EdroneInput>
        <EdroneInput
          :label="trans._inbox.customer.properties.activity"
          :placeholder="trans._inbox.customer.properties.placeholders.noData"
          :value="getInputValue('activitySegment')"
          disabled
          unique-id="bcb93786-0e23-4b5a-87d5-74b340e0a301"
        >
          <template #tooltip>
            <EdroneTooltip
              position="top"
              trigger-event="mouseenter click"
              type="custom"
              unique-id="01993597-33c3-4250-aeca-221d9e2d6563"
            >
              <template #custom>
                <p class="tw:caption">
                  {{ trans._inbox.customer.properties.helpers.activity }}
                  <EdroneButtonIcon
                    size="small"
                    type="link"
                    unique-id="e14f9faa-2959-4760-bdf7-2181b1b5a093"
                    @click="goToDoc"
                  >
                    <font-awesome-icon icon="fa-light fa-link" />
                  </EdroneButtonIcon>
                </p>
              </template>
            </EdroneTooltip>
          </template>
        </EdroneInput>
      </div>
    </div>

    <EdroneModal
      :open="isOpenSubscriptionConfirmationDialog"
      unique-id="920dd628-c992-43ff-b626-b7a14c5558dc"
      @close="cancelDialog"
    >
      <template #title>{{ trans._inbox.customer.properties.subscriptions.confirmation.title }}</template>
      <p class="tw:body-2 tw:mb-4 tw:text-default-700">
        {{ trans._inbox.customer.properties.subscriptions.confirmation.description }}
      </p>
      <div class="tw:flex tw:gap-2">
        <EdroneButton
          class="tw:flex-1"
          type="outline"
          unique-id="a52322d4-41d7-4d60-9760-c66f773f374f"
          @click="cancelDialog"
        >
          {{ trans._inbox.customer.properties.subscriptions.confirmation.cancel }}
        </EdroneButton>
        <EdroneButton
          class="tw:flex-1"
          type="solid"
          unique-id="0dc35988-137a-45f5-a768-4269554d1d8e"
          @click="confirmDialog"
        >
          {{ trans._inbox.customer.properties.subscriptions.confirmation.confirm }}
        </EdroneButton>
      </div>
    </EdroneModal>
  </section>
</template>

<script lang="ts">
import { computed, defineComponent, ref } from "vue";
import MetricsSmallCard from "vue-app/components/Metrics/MetricsSmallCard.vue";
import { Purchase, useCustomerPreviewActor } from "vue-app/pages/machines/machineCustomerPreview";
import { dayjsWithTz } from "vue-app/utils/functions";
import { disabledAfterToday } from "vue-app/datesDatepicker/datesDatepicker";
import EdroneButtonIcon from "edrone-components/EdroneButtonIcon.vue";
import EdroneInput from "edrone-components/EdroneInput.vue";
import EdroneSelect from "edrone-components/EdroneSelect.vue";
import EdroneDatepicker from "edrone-components/EdroneDatepicker.vue";
import EdroneTooltip from "edrone-components/EdroneTooltip.vue";
import EdroneModal from "edrone-components/EdroneModal.vue";
import EdroneButton from "edrone-components/EdroneButton.vue";
import { Dayjs } from "dayjs";

export default defineComponent({
  name: "CustomerPreviewProperties",
  components: {
    EdroneButtonIcon,
    EdroneInput,
    EdroneSelect,
    EdroneDatepicker,
    EdroneTooltip,
    MetricsSmallCard,
    EdroneModal,
    EdroneButton,
  },
  setup() {
    const { state, send } = useCustomerPreviewActor();
    const currency = window.EDRONE_CONTEXT.user.currency;

    const updateCustomer = (property: string, value: string) => {
      send({ type: "UPDATE_FORM", property, value });
    };

    const isOpenSubscriptionConfirmationDialog = ref(false);
    const editingStatus = ref<null | { type: string; status: string }>(null);
    const refreshSMSSelect = ref(Math.random()); // needed to display the selected option always
    const refreshEmailSelect = ref(Math.random()); // equal to the property (sms/email/whatsapp status) in the form object
    const refreshWhatsappSelect = ref(Math.random()); // equal to the property (sms/email/whatsapp status) in the form object

    const subscriptionStatusArray = ref([
      { name: window._trans._inbox.customer.properties.subscriptions.status.subscribed, value: "SUBSCRIBED" },
      { name: window._trans._inbox.customer.properties.subscriptions.status.unsubscribed, value: "UNSUBSCRIBED" },
      { name: window._trans._inbox.customer.properties.subscriptions.status.unknown, value: "UNKNOWN" },
    ]);

    const subscriptionEmailStatus = computed(() => {
      return (
        subscriptionStatusArray.value.find(
          (status) => status.value === state.value.context.form?.subscriptionEmailStatus
        ) || []
      );
    });

    const subscriptionSMSStatus = computed(() => {
      return (
        subscriptionStatusArray.value.find(
          (status) => status.value === state.value.context.form?.subscriptionSMSStatus
        ) || []
      );
    });

    const subscriptionWhatsappStatus = computed(() => {
      return (
        subscriptionStatusArray.value.find(
          (status) => status.value === state.value.context.form?.subscriptionWhatsappStatus
        ) || []
      );
    });

    const openDialog = (type: string, status: string) => {
      editingStatus.value = {
        type,
        status,
      };
      isOpenSubscriptionConfirmationDialog.value = true;
      refreshEmailSelect.value = Math.random();
      refreshSMSSelect.value = Math.random();
      refreshWhatsappSelect.value = Math.random();
    };

    const confirmDialog = () => {
      if (!editingStatus.value) return;
      send({ type: "UPDATE_FORM", property: editingStatus.value.type, value: editingStatus.value.status });
      editingStatus.value = null;
      isOpenSubscriptionConfirmationDialog.value = false;
    };

    const cancelDialog = () => {
      editingStatus.value = null;
      isOpenSubscriptionConfirmationDialog.value = false;
    };

    const genderTypeArray = ref([
      { name: window._trans._inbox.customer.properties.genderOption.male, value: "MALE" },
      { name: window._trans._inbox.customer.properties.genderOption.female, value: "FEMALE" },
      { name: window._trans._inbox.customer.properties.genderOption.other, value: "OTHER" },
      { name: window._trans._inbox.customer.properties.genderOption.unisex, value: "UNISEX" },
      { name: window._trans._inbox.customer.properties.genderOption.unknown, value: "UNKNOWN" },
      { name: window._trans._inbox.customer.properties.genderOption.refused, value: "REFUSED_TO_PASS" },
    ]);

    const genderType = computed(() => {
      return genderTypeArray.value.find((status) => status.value === state.value.context.form?.genderType) || [];
    });

    const languages = [
      "af",
      "sq",
      "ar",
      "eu",
      "bg",
      "be",
      "ca",
      "zh",
      "hr",
      "cs",
      "da",
      "nl",
      "en",
      "et",
      "fo",
      "fa",
      "fi",
      "fr",
      "gd",
      "de",
      "el",
      "he",
      "hi",
      "hu",
      "is",
      "id",
      "it",
      "ja",
      "ko",
      "lv",
      "lt",
      "mk",
      "mt",
      "pl",
      "pt",
      "rm",
      "ro",
      "ru",
      "sz",
      "sr",
      "sk",
      "sl",
      "sb",
      "es",
      "sx",
      "sv",
      "th",
      "ts",
      "tn",
      "tr",
      "uk",
      "ur",
      "ve",
      "vi",
      "xh",
      "ji",
      "zu",
      "nb",
    ];

    const languagesArray = ref(
      languages.map((lang) => {
        return {
          value: lang,
          name: window._trans[lang],
        };
      })
    );

    const language = computed(() => {
      return languagesArray.value.find((status) => status.value === state.value.context.form?.language) || [];
    });

    const getDateFormat = (date: number | Dayjs) => {
      return date ? dayjsWithTz(date).format("MMM DD, YYYY HH:ss a") : "-";
    };

    const getCurrency = () => {
      return window.EDRONE_CONTEXT.user.currency;
    };

    const getLastVisitDateFormatted = () => {
      return state.value.context.customer?.purchasing.lastVisitDate
        ? dayjsWithTz(state.value.context.customer?.purchasing.lastVisitDate).format("MMM DD, YYYY HH:ss a")
        : "";
    };

    const getInputValue = (key: string) => {
      return state.value.context.customer?.purchasing[key as keyof Purchase]
        ? formatValue(state.value.context.customer?.purchasing[key as keyof Purchase])
        : "";
    };

    const formatValue = (value: string | number) => {
      return typeof value === "string" ? value.replaceAll("_", " ") : value;
    };

    function goToDoc() {
      window.open("https://help.edrone.me/en/articles/2412412-segmentation-possibilities-in-edrone", "_blank");
    }

    return {
      state,
      currency,
      updateCustomer,
      subscriptionEmailStatus,
      subscriptionSMSStatus,
      subscriptionWhatsappStatus,
      subscriptionStatusArray,
      isOpenSubscriptionConfirmationDialog,
      openDialog,
      cancelDialog,
      confirmDialog,
      refreshSMSSelect,
      refreshWhatsappSelect,
      refreshEmailSelect,
      getDateFormat,
      disabledAfterToday,
      genderTypeArray,
      genderType,
      languagesArray,
      language,
      getLastVisitDateFormatted,
      getCurrency,
      getInputValue,
      goToDoc,
    };
  },
});
</script>

<style lang="scss" scoped></style>
<style lang="scss">
.mx-datepicker-popup {
  z-index: 999999999999 !important;
}
</style>
